import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SectionHeader from "@/components/ui/SectionHeader";
import { Check } from "lucide-react";

const studios = [
  {
    name: "Studio A",
    rate: "$45",
    min: "1 hour",
    desc: "Our versatile photography studio perfect for portraits, headshots, product photography, and content creation.",
    features: ["Seamless paper backdrops (multiple colors)", "Professional lighting kits", "Tether station & monitor", "Private changing area", "High-speed WiFi", "Bluetooth speaker", "Props available (+$25)"],
    img: "https://images.unsplash.com/photo-1604514628550-37477afdf4e3?w=800&q=80",
    slug: "studio-a",
  },
  {
    name: "Studio B",
    rate: "$70",
    min: "2 hours",
    desc: "Our premium production studio featuring a full cyc wall, perfect for film, video, and editorial-grade shoots.",
    features: ["Full cyclorama wall", "Green screen available (+$20)", "Film-grade continuous lighting", "4K-ready production setup", "Audio dampening", "Dedicated power circuits", "In-house facilitator available (+$50/hr)"],
    img: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?w=800&q=80",
    slug: "studio-b",
  },
];

const faqs = [
  { q: "What's included in a studio rental?", a: "All rentals include access to the studio space, basic lighting equipment, WiFi, and a bluetooth speaker. Additional equipment and services can be added during booking." },
  { q: "What is your cancellation policy?", a: "Cancellations made 48+ hours in advance receive a full deposit refund. Cancellations within 48 hours forfeit the deposit. No-shows are charged the full booking amount." },
  { q: "Is there parking available?", a: "Yes, free parking is available directly in front of our studio space at 2300 Whittlesey Rd Ste E, Columbus, GA." },
  { q: "Can I bring my own equipment?", a: "Absolutely! You're welcome to bring any personal equipment. We just ask that you handle our studio equipment with care." },
  { q: "What are the studio rules?", a: "No smoking, no open flames, clean up after your session, and be respectful of equipment. Full rules are provided during booking." },
];

const StudiosPage = () => {
  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="py-20 lg:py-28 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">OUR STUDIOS</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-body">
            Two purpose-built creative spaces designed for photographers, videographers, and content creators.
          </p>
        </div>
      </section>

      {/* Studios */}
      <section className="pb-20">
        <div className="container mx-auto px-4 space-y-16">
          {studios.map((studio) => (
            <div key={studio.name} className="grid grid-cols-1 lg:grid-cols-2 gap-8 bg-panel border border-border overflow-hidden">
              <div className="aspect-video lg:aspect-auto">
                <img src={studio.img} alt={studio.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 flex flex-col justify-center">
                <div className="flex items-center gap-4 mb-4">
                  <h2 className="text-3xl font-display tracking-wider">{studio.name}</h2>
                  <span className="bg-primary/10 text-primary px-4 py-1 font-mono font-medium">{studio.rate}/hr</span>
                </div>
                <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-4">Minimum: {studio.min}</p>
                <p className="text-muted-foreground font-body mb-6">{studio.desc}</p>
                <ul className="space-y-2 mb-8">
                  {studio.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to={`/book?studio=${studio.slug}`}>
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-8">
                    Book {studio.name}
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-surface">
        <div className="container mx-auto px-4 max-w-3xl">
          <SectionHeader title="FREQUENTLY ASKED" />
          <div className="space-y-4">
            {faqs.map((faq) => (
              <details key={faq.q} className="bg-panel border border-border group">
                <summary className="p-5 cursor-pointer font-display tracking-wider text-lg list-none flex items-center justify-between">
                  {faq.q}
                  <span className="text-primary transition-transform group-open:rotate-45 text-2xl">+</span>
                </summary>
                <div className="px-5 pb-5 text-muted-foreground font-body text-sm">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 text-center">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-5xl font-display tracking-widest mb-6">READY TO CREATE?</h2>
          <Link to="/book">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-body font-semibold px-10 text-lg">
              Book Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default StudiosPage;
